import { CSSProperties } from 'react';
import { CalendarDays, FileStack, Handshake, ListTodo, MessageCircleMore } from 'lucide-react';
import {
    TbAddressBook,
    TbCalendar,
    TbFileSpreadsheet,
    TbFileText,
    TbLayout,
    TbLibrary,
    TbMail,
    TbMessage,
    TbPackage,
} from 'react-icons/tb';

import {
    AirtableSVGIcon,
    AsanaSVGIcon,
    AttioSVGIcon,
    BoxSVGIcon,
    ClickUpSVGICON,
    CloseCRMSVGIcon,
    ConfluenceSVGIcon,
    DocuSignSVGIcon,
    DropboxSVGIcon,
    GmailSVGIcon,
    GoogleDriveSVGIcon,
    GoogleSheetsSVGIcon,
    HubSpotSVGIcon,
    MicrosoftOutlookSVGIcon,
    MondayDotComSVGIcon,
    NotionSVGIcon,
    PipedriveSVGIcon,
    SalesforceSVGIcon,
    SlackSVGIcon,
    SmartsheetSVGIcon,
    // WealthboxSVGIcon,
    WrikeSVGIcon,
} from '@/assets';
import PlatformAlert from '@/shared/components/alerts/PlatformAlert';
import OurDocsLink from '@/shared/components/tags/OurDocsLink';
import { ConnectedAccountType, DataSourceSection } from '@/types';

export interface DataSourceConnectionParams {
    provider?: string;
    [key: string]: string | number | boolean | undefined;
}

interface CreateDataSourceSectionsParams {
    categoryIconStyle: CSSProperties;
    handleConnectDataSource: (option: string, params?: DataSourceConnectionParams) => Promise<void>;
    sectionIconStyle: CSSProperties;
}

export const createDataSourceSections = ({
    categoryIconStyle,
    handleConnectDataSource,
    sectionIconStyle,
}: CreateDataSourceSectionsParams): DataSourceSection[] =>
    [
        {
            key: 'emailAndCalendar',
            label: 'Email & Calendar',
            icon: <CalendarDays size='1rem' style={{ ...sectionIconStyle }} />,
            hint: (
                <PlatformAlert
                    message={<>Combined Email & Calendar Syncing</>}
                    status='info'
                    description={
                        <>
                            <div>
                                Countercyclical supports email & calendar syncing for{' '}
                                <OurDocsLink
                                    text={'Gmail'}
                                    href='https://workspace.google.com/intl/en-US/products/gmail/'
                                />
                                ,{' '}
                                <OurDocsLink
                                    text={'Google Calendar'}
                                    href='https://workspace.google.com/intl/en-US/products/calendar/'
                                />
                                , and{' '}
                                <OurDocsLink
                                    text={'Microsoft 365 Outlook Email & Calendar'}
                                    href='https://www.microsoft.com/en-us/microsoft-365/outlook/email-and-calendar-software-microsoft-outlook'
                                />
                            </div>
                            <div style={{ marginTop: '0.5rem' }}>
                                By default, synced Email & Calendar records come from sent email recipients & past
                                calendar event attendees. For more information, please visit <OurDocsLink />
                            </div>
                        </>
                    }
                    styles={{ marginBottom: '0.5rem', marginTop: '1rem', padding: '1rem' }}
                />
            ),
            options: [
                {
                    key: ConnectedAccountType.GMAIL, // !IMPORTANT: Incorrect
                    label: 'Gmail',
                    active: true,
                    categories: [
                        { key: 'email', label: 'Email', icon: <TbMail style={{ ...categoryIconStyle }} /> },
                        { key: 'calendar', label: 'Calendar', icon: <TbCalendar style={{ ...categoryIconStyle }} /> },
                        {
                            key: 'contacts',
                            label: 'Contacts',
                            icon: <TbAddressBook style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'productivity',
                            label: 'Productivity',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: GmailSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('nylas', { provider: 'google' });
                    },
                    officialIntegration: true,
                    featureAddOnRequiredForUse: true,
                    requiredAddOnsForUse: ['Relations'],
                },
                {
                    key: ConnectedAccountType.MICROSOFT_365_OUTLOOK, // !IMPORTANT: Incorrect
                    active: true,
                    label: 'Microsoft Outlook',
                    categories: [
                        { key: 'email', label: 'Email', icon: <TbMail style={{ ...categoryIconStyle }} /> },
                        { key: 'calendar', label: 'Calendar', icon: <TbCalendar style={{ ...categoryIconStyle }} /> },
                        {
                            key: 'contacts',
                            label: 'Contacts',
                            icon: <TbAddressBook style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'productivity',
                            label: 'Productivity',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: MicrosoftOutlookSVGIcon,
                    avatarStyling: { transform: 'scale(0.85)' },
                    handler: async () => {
                        await handleConnectDataSource('nylas', { provider: 'microsoft' });
                    },
                    officialIntegration: true,
                    featureAddOnRequiredForUse: true,
                    requiredAddOnsForUse: ['Relations'],
                },
            ],
        },
        {
            key: 'documents',
            label: 'Document Storage',
            icon: <FileStack size='1rem' style={{ ...sectionIconStyle }} />,
            options: [
                {
                    key: ConnectedAccountType.GOOGLE_DRIVE,
                    label: 'Google Drive',
                    categories: [
                        {
                            key: 'documentStorage',
                            label: 'Document Storage',
                            icon: <TbPackage style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: GoogleDriveSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('google-drive');
                    },
                    officialIntegration: true,
                    featureAddOnRequiredForUse: false,
                },
                {
                    key: ConnectedAccountType.DROPBOX,
                    label: 'Dropbox',
                    categories: [
                        {
                            key: 'documentStorage',
                            label: 'Document Storage',
                            icon: <TbPackage style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: DropboxSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('dropbox');
                    },
                    officialIntegration: true,
                    featureAddOnRequiredForUse: false,
                },
                {
                    key: ConnectedAccountType.BOX,
                    label: 'Box',
                    categories: [
                        {
                            key: 'documentStorage',
                            label: 'Document Storage',
                            icon: <TbPackage style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: BoxSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('box');
                    },
                    officialIntegration: true,
                    featureAddOnRequiredForUse: false,
                },
            ],
        },
        {
            key: 'collaboration',
            label: 'Collaboration',
            icon: <MessageCircleMore size='1rem' style={{ ...sectionIconStyle }} />,
            options: [
                {
                    key: ConnectedAccountType.SLACK,
                    label: 'Slack',
                    categories: [
                        {
                            key: 'teamChat',
                            label: 'Team Chat',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: SlackSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('slack');
                    },
                    officialIntegration: true,
                },
            ],
        },
        {
            key: 'productivity',
            label: 'Productivity',
            icon: <ListTodo size='1rem' style={{ ...sectionIconStyle }} />,
            options: [
                {
                    key: ConnectedAccountType.GOOGLE_SHEETS,
                    label: 'Google Sheets',
                    categories: [
                        {
                            key: 'spreadsheets',
                            label: 'Spreadsheets',
                            icon: <TbFileSpreadsheet style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'productivity',
                            label: 'Productivity',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: GoogleSheetsSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('google-sheets');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.NOTION,
                    label: 'Notion',
                    categories: [
                        {
                            key: 'knowledge',
                            label: 'Knowledge Management',
                            icon: <TbLibrary style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'documentation',
                            label: 'Documentation',
                            icon: <TbFileText style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: NotionSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('notion');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.DOCUSIGN,
                    label: 'DocuSign',
                    categories: [
                        {
                            key: 'electronicSignature',
                            label: 'Electronic Signature',
                            icon: <TbFileText style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'documentation',
                            label: 'Documentation',
                            icon: <TbLibrary style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: DocuSignSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('docusign');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.AIRTABLE,
                    label: 'Airtable',
                    categories: [
                        {
                            key: 'projectManagement',
                            label: 'Project Management',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                        { key: 'database', label: 'Database', icon: <TbPackage style={{ ...categoryIconStyle }} /> },
                    ],
                    src: AirtableSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('airtable');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.ASANA,
                    label: 'Asana',
                    categories: [
                        {
                            key: 'projectManagement',
                            label: 'Project Management',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: AsanaSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('asana');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.WRIKE,
                    label: 'Wrike',
                    categories: [
                        {
                            key: 'projectManagement',
                            label: 'Project Management',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: WrikeSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('wrike');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.CLICKUP,
                    label: 'ClickUp',
                    categories: [
                        {
                            key: 'projectManagement',
                            label: 'Project Management',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'collaboration',
                            label: 'Collaboration',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: ClickUpSVGICON,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('clickup');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.MONDAY,
                    label: 'Monday.com',
                    categories: [
                        {
                            key: 'projectManagement',
                            label: 'Project Management',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                        { key: 'boards', label: 'Boards', icon: <TbLayout style={{ ...categoryIconStyle }} /> },
                    ],
                    src: MondayDotComSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('monday');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.CONFLUENCE,
                    label: 'Confluence',
                    categories: [
                        {
                            key: 'knowledge',
                            label: 'Knowledge Management',
                            icon: <TbLibrary style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'documentation',
                            label: 'Documentation',
                            icon: <TbFileText style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: ConfluenceSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('confluence');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.SMARTSHEET,
                    label: 'Smartsheet',
                    categories: [
                        {
                            key: 'spreadsheets',
                            label: 'Spreadsheets',
                            icon: <TbFileSpreadsheet style={{ ...categoryIconStyle }} />,
                        },
                        {
                            key: 'projectManagement',
                            label: 'Project Management',
                            icon: <TbLayout style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: SmartsheetSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('smartsheet');
                    },
                    officialIntegration: true,
                },
            ],
        },
        {
            key: 'crm',
            label: 'CRM',
            icon: <Handshake size='1rem' style={{ ...sectionIconStyle }} />,
            options: [
                {
                    key: ConnectedAccountType.SALESFORCE,
                    label: 'Salesforce',
                    categories: [
                        { key: 'crm', label: 'CRM', icon: <TbAddressBook style={{ ...categoryIconStyle }} /> },
                        { key: 'sales', label: 'Sales', icon: <TbPackage style={{ ...categoryIconStyle }} /> },
                    ],
                    src: SalesforceSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('salesforce');
                    },
                    officialIntegration: true,
                    minimumPlanForUse: 'premium',
                },
                {
                    key: ConnectedAccountType.HUBSPOT,
                    label: 'HubSpot',
                    categories: [
                        { key: 'crm', label: 'CRM', icon: <TbAddressBook style={{ ...categoryIconStyle }} /> },
                        {
                            key: 'relationshipManagement',
                            label: 'Relationship Management',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: HubSpotSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('hubspot');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.PIPEDRIVE,
                    label: 'Pipedrive',
                    categories: [
                        { key: 'crm', label: 'CRM', icon: <TbAddressBook style={{ ...categoryIconStyle }} /> },
                        { key: 'sales', label: 'Sales', icon: <TbPackage style={{ ...categoryIconStyle }} /> },
                    ],
                    src: PipedriveSVGIcon,
                    avatarStyling: { transform: 'scale(1)' },
                    handler: async () => {
                        await handleConnectDataSource('pipedrive');
                    },
                    officialIntegration: true,
                },
                {
                    key: ConnectedAccountType.ATTIO,
                    label: 'Attio',
                    categories: [
                        { key: 'crm', label: 'CRM', icon: <TbAddressBook style={{ ...categoryIconStyle }} /> },
                        {
                            key: 'relationshipManagement',
                            label: 'Relationship Management',
                            icon: <TbMessage style={{ ...categoryIconStyle }} />,
                        },
                    ],
                    src: AttioSVGIcon,
                    avatarStyling: { transform: 'scale(1)' },
                    handler: async () => {
                        await handleConnectDataSource('attio');
                    },
                    officialIntegration: true,
                },
                // Wealthbox remains implemented, but is intentionally unavailable as a new Data Source for now.
                // {
                //     key: ConnectedAccountType.WEALTHBOX,
                //     label: 'Wealthbox',
                //     categories: [
                //         { key: 'crm', label: 'CRM', icon: <TbAddressBook style={{ ...categoryIconStyle }} /> },
                //         {
                //             key: 'relationshipManagement',
                //             label: 'Relationship Management',
                //             icon: <TbMessage style={{ ...categoryIconStyle }} />,
                //         },
                //     ],
                //     src: WealthboxSVGIcon,
                //     avatarStyling: { transform: 'scale(0.75)' },
                //     handler: async () => {
                //         await handleConnectDataSource('wealthbox');
                //     },
                //     officialIntegration: true,
                // },
                {
                    key: ConnectedAccountType.CLOSE,
                    label: 'Close',
                    categories: [
                        { key: 'crm', label: 'CRM', icon: <TbAddressBook style={{ ...categoryIconStyle }} /> },
                        { key: 'sales', label: 'Sales', icon: <TbPackage style={{ ...categoryIconStyle }} /> },
                    ],
                    src: CloseCRMSVGIcon,
                    avatarStyling: { transform: 'scale(0.75)' },
                    handler: async () => {
                        await handleConnectDataSource('close');
                    },
                    officialIntegration: true,
                },
            ],
        },
    ] as DataSourceSection[];
